/**
 * IconButton — the nine icon-only controls.
 *
 * `Oprappo Design System.dc.html § 01` — "আইকন-মাত্র নিয়ন্ত্রণের নাম":
 *   "যে নিয়ন্ত্রণে কোনো দৃশ্যমান লেখা নেই, তার একটি বাংলা নাম আবশ্যক — স্ক্রিন রিডার
 *    সেটিই পড়বে। নাম স্পেকে লেখা থাকবে, ডেভেলপারের অনুমানের উপর ছাড়া হবে না।"
 *
 * The accessible name is therefore NOT a prop. It is read from `ariaLabel` in
 * `@oprappo/tokens`, which is the code form of the § 01 table. There is deliberately
 * no way to pass a different string.
 */
import { ariaLabel } from '@oprappo/tokens';
import { Icon } from './Icon.js';
import type { IconName } from './Icon.js';

/** The nine controls listed in § 01. */
export type IconOnlyControl =
  | 'voiceSearch'
  | 'imageSearch'
  | 'bell'
  | 'back'
  | 'close'
  | 'more'
  | 'send'
  | 'avatar'
  | 'lock';

const ICON_FOR: Record<IconOnlyControl, IconName> = {
  voiceSearch: 'voice',
  imageSearch: 'image',
  bell: 'bell',
  back: 'chevronLeft',
  close: 'close',
  more: 'more',
  send: 'send',
  avatar: 'account',
  lock: 'lock',
};

interface IconButtonBase {
  /** Rendered icon size in px — taken from the frame being built. */
  size: number;
  /** § 01: "no colour except state". Defaults to ink-teal, as drawn. */
  stroke?: string | undefined;
  onClick?: (() => void) | undefined;
  className?: string | undefined;
}

export type IconButtonProps =
  | (IconButtonBase & {
      control: 'bell';
      /** The bell's name carries the count: `খবর — ৩টি নতুন`. */
      count: number;
    })
  | (IconButtonBase & { control: Exclude<IconOnlyControl, 'bell'> });

export function IconButton(props: IconButtonProps): JSX.Element {
  const { control, size, stroke = '#0E3A45', onClick, className } = props;
  const label = control === 'bell' ? ariaLabel.bell(props.count) : ariaLabel[control];

  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={
        'inline-flex items-center justify-center bg-transparent border-0 p-0' +
        (className === undefined ? '' : ' ' + className)
      }
    >
      <Icon name={ICON_FOR[control]} size={size} stroke={stroke} />
    </button>
  );
}
